import React, { createContext, useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import UpgradePrompt from './UpgradePrompt';

interface QuotaExceededInfo {
  resource?: 'workflows' | 'hosts' | 'users' | 'storage';
  message?: string;
  details?: {
    resource?: string;
    current?: number;
    limit?: number;
  };
}

interface QuotaExceededContextValue {
  showUpgradePrompt: (info: QuotaExceededInfo) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const QuotaExceededContext = createContext<QuotaExceededContextValue | undefined>(undefined);

/**
 * Provider component that manages global 402 (quota exceeded) error handling
 */
export function QuotaExceededProvider({ children }: { children: React.ReactNode }) {
  const [upgradePromptOpen, setUpgradePromptOpen] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState<QuotaExceededInfo>({});
  const queryClient = useQueryClient();

  const showUpgradePrompt = useCallback((info: QuotaExceededInfo) => {
    setQuotaInfo(info);
    setUpgradePromptOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setUpgradePromptOpen(false);
    setQuotaInfo({});
  }, []);

  // Set up global error monitoring for 402 errors
  // Note: Individual mutation hooks should catch 402 errors and call showUpgradePrompt
  // This effect is just to ensure queryClient is available
  useEffect(() => {
    // Make queryClient available for hooks to use
    // Individual hooks will handle 402 errors in their onError callbacks
  }, [queryClient]);

  return (
    <QuotaExceededContext.Provider value={{ showUpgradePrompt }}>
      {children}
      <UpgradePrompt
        isOpen={upgradePromptOpen}
        onClose={handleClose}
        resource={quotaInfo.resource}
        message={quotaInfo.message}
      />
    </QuotaExceededContext.Provider>
  );
}
